import { getToken } from '../../session';
import type {
  Simulation,
  SimulationUpload,
  SimulationUpdate,
  SimulationDetail,
  RateUpdate,
  SimulationRawResult,
  SimulationBatchUpdate,
  SimulationShipmentBatch,
  SimulationExportMetadata,
  CreateOriginSimulationPayload,
  SimulationContextMappingCreate,
  CarrierAndMethodsSimulationPayload,
  SimulationContextMapping,
  FetchSimulationReportParams,
  SimulationReport,
  SellRateSimulationPayload,
  CarrierZoneToSellRateZoneMapping,
  SearchCarrierZoneToSellRateZoneMappingParams,
  DeleteSellRateMappingsRes,
} from '../../simulation/types';
import type { AdvancedSimulationPayload } from '../../simulation/utils';
import GatewayAxiosClient from '../GatewayAxiosClient';

export class SimulationService {
  // todo figure out what is allowed here
  public async fetchSimulations(fetchParams: any): Promise<Simulation[]> {
    return [];
  }

  public async fetchSimulationDetail(simulationId: string): Promise<SimulationDetail> {
    return (
      await GatewayAxiosClient.get(`/shipium-internal-proxy/simulation/${simulationId}`, {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      })
    ).data;
  }

  public async fetchSimulationRawResults(
    simulationId: string,
    date: string,
    hour: string,
    status: string,
    page: string,
  ): Promise<SimulationRawResult> {
    const offset = ((parseInt(page, 10) || 1) - 1) * 50;
    return (
      await GatewayAxiosClient.get(
        `/shipium-internal-proxy/simulation/${simulationId}/raw-results?date=${date}&hour=${hour}&status=${status}&offset=${offset}`,
        {
          headers: {
            authorization: `Bearer ${getToken()}`,
          },
        },
      )
    ).data;
  }

  public async fetchSimulationSummaries(partnerId: string): Promise<Simulation[]> {
    return (
      await GatewayAxiosClient.get(`/shipium-internal-proxy/simulation/simulation-summary/${partnerId}`, {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      })
    ).data;
  }

  public async fetchSimulationUploads(partnerId: string): Promise<SimulationUpload[]> {
    return (
      await GatewayAxiosClient.get(`/shipium-internal-proxy/simulation/${partnerId}/uploads`, {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      })
    ).data;
  }

  public async deleteSimulationUpload({ partnerId, s3Key }: { partnerId: string; s3Key: string }) {
    return (await GatewayAxiosClient.delete(`/shipium-internal-proxy/simulation/${partnerId}/uploads/${s3Key}`)).data;
  }

  public async createSimulation(sim: Partial<Simulation>): Promise<Simulation> {
    return (
      await GatewayAxiosClient.post(`/shipium-internal-proxy/simulation`, sim, {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      })
    ).data;
  }

  public async updateSimulation(sim: SimulationUpdate): Promise<Simulation> {
    return (
      await GatewayAxiosClient.put(`/shipium-internal-proxy/simulation/${sim.simulationId}`, sim, {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      })
    ).data;
  }

  public async updateSimulationShipmentBatch(batch: SimulationBatchUpdate): Promise<SimulationShipmentBatch> {
    return (
      await GatewayAxiosClient.put(
        `/shipium-internal-proxy/simulation/${batch.simulationId}/simulation-shipment-batch/${batch.simulationBatchId}`,
        batch,
        {
          headers: {
            authorization: `Bearer ${getToken()}`,
          },
        },
      )
    ).data;
  }

  public async fetchSimulationExports(simulationId: string): Promise<SimulationExportMetadata[]> {
    return (
      await GatewayAxiosClient.get(`/shipium-internal-proxy/simulation/${simulationId}/exports-list`, {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      })
    ).data;
  }

  public async fetchSimulationReport(params: FetchSimulationReportParams): Promise<SimulationReport> {
    const {
      simulationId,
      reportType,
      originSlug,
      dayOfWeek,
      tenantId,
      tntType,
      carrier,
      sellCarrierRateId,
      includeCarrierSurcharges = true,
    } = params;

    // OTD reports route through /simulation-proxy (simulation-service directly);
    // other reports use /shipium-internal-proxy (via shipium-http-api-internal).
    const path =
      reportType === 'otd-report'
        ? `/simulation-proxy/api/v1/simulation/${simulationId}/reports/${reportType}`
        : `/shipium-internal-proxy/simulation/${simulationId}/reports/${reportType}`;

    return (
      await GatewayAxiosClient.get(path, {
        params: {
          ...(originSlug ? { originSlug } : {}),
          ...(dayOfWeek ? { dayOfWeek } : {}),
          ...(tenantId ? { tenantId } : {}),
          ...(tntType ? { tntType } : {}),
          ...(carrier ? { carrier } : {}),
          ...(sellCarrierRateId ? { sellCarrierRateId } : {}),
          includeCarrierSurcharges,
        },
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      })
    ).data;
  }

  public async createSimulationContextMappings(
    simulationContextCreate: SimulationContextMappingCreate[],
  ): Promise<SimulationContextMapping[]> {
    const simulationId = simulationContextCreate[0]?.simulationId;
    if (!simulationId) {
      throw new Error('Unable to create context mappings. No simulation detected');
    }
    return (
      await GatewayAxiosClient.post(
        `/shipium-internal-proxy/simulation/simulation-context-mapping/${simulationId}`,
        simulationContextCreate,
        {
          headers: {
            authorization: `Bearer ${getToken()}`,
          },
        },
      )
    ).data;
  }

  public async validateSimulationUpload(s3Key: string): Promise<SimulationUpload> {
    return (
      await GatewayAxiosClient.post(
        `/shipium-internal-proxy/simulations/uploads/process-shipment-upload`,
        { s3Key },
        {
          headers: {
            authorization: `Bearer ${getToken()}`,
          },
        },
      )
    ).data;
  }

  public async createSimulationBatches(simulationId: string) {
    return (
      await GatewayAxiosClient.get(`/shipium-internal-proxy/simulation/${simulationId}/generate-shipment-batches`, {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      })
    ).data;
  }

  public async fetchFulfillmentContextIDsByMethod(carrierId: string, serviceMethodId: string) {
    return (
      await GatewayAxiosClient.get(`/shipium-internal-proxy/partner/contexts-for-method`, {
        params: {
          carrierId,
          serviceMethodId,
        },
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      })
    ).data;
  }

  public async archiveSimulation(simulationId: string) {
    return (await GatewayAxiosClient.delete(`/shipium-internal-proxy/simulation/${simulationId}`)).data;
  }

  public async startSimulation(
    payload:
      | CreateOriginSimulationPayload
      | RateUpdate
      | CarrierAndMethodsSimulationPayload
      | AdvancedSimulationPayload
      | SellRateSimulationPayload,
  ) {
    return (
      await GatewayAxiosClient.post(`/shipium-internal-proxy/simulation/${payload.simulationId}/start-simulation`, {
        simulationAssetCreatePayload: payload,
      })
    ).data;
  }

  public async fetchSimulationSellRateMappings(simulationId: string) {
    return (await GatewayAxiosClient.get(`/shipium-internal-proxy/simulation/${simulationId}/simulation-sell-rates`))
      .data;
  }

  public async fetchSimulationSellRateZoneMappings(simulationId: string, sellCarrierRateId: string) {
    return (
      await GatewayAxiosClient.get(
        `/shipium-internal-proxy/simulation/${simulationId}/carrier-zone-mappings/${sellCarrierRateId}`,
      )
    ).data;
  }

  public async createSimulationSellRateZoneMappings(mappings: Partial<CarrierZoneToSellRateZoneMapping>[]) {
    return (await GatewayAxiosClient.post('/shipium-internal-proxy/simulation/carrier-zone-mappings', mappings)).data;
  }

  public async searchCarrierSellRateMappings(
    params: SearchCarrierZoneToSellRateZoneMappingParams,
  ): Promise<{ params: any; mappings: CarrierZoneToSellRateZoneMapping[] }> {
    const { page, pageSize, sellRateCarrierServiceMethodId, sellRateZoneId, carrierId } = params;
    return (
      await GatewayAxiosClient.get('/shipium-internal-proxy/simulation/carrier-zone-mappings', {
        params: {
          ...(page ? { page } : {}),
          ...(pageSize ? { pageSize } : {}),
          ...(sellRateCarrierServiceMethodId ? { sellRateCarrierServiceMethodId } : {}),
          ...(sellRateZoneId ? { sellRateZoneId } : {}),
          ...(carrierId ? { carrierId } : {}),
        },
      })
    ).data;
  }

  public async deleteSellRateMappings(
    params: SearchCarrierZoneToSellRateZoneMappingParams,
  ): Promise<DeleteSellRateMappingsRes> {
    return (
      await GatewayAxiosClient.delete(`/shipium-internal-proxy/simulation/carrier-zone-mappings`, {
        data: {
          ...params,
        },
      })
    ).data;
  }

  public async cleanupSimulationAssets(simulationId: string): Promise<{ deletedAssets: number }> {
    return (
      await GatewayAxiosClient.delete(`/partner-management-proxy/api/v1/partners/simulation/cleanup/${simulationId}`)
    ).data;
  }
}
